import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CompanyVoteProposalNameSearchFilter } from '../models/companyVoteProposalNameSearchFilter';
import { PaginatedResponse } from '../models/paginatedResponse';
import { CompanyVoteProposalNameDto } from '../models/api/companyVoteProposalNameDto';
import { CreateCompanyVoteProposalNameRequest } from '../models/api/createCompanyVoteProposalNameRequest';
import { UpdateCompanyVoteProposalNameRequest } from '../models/api/updateCompanyVoteProposalNameRequest';
import { BulkMergeRequest } from '../models/bulkMergeRequest';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CompanyVoteProposalNameService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/company-vote-proposal-names`;

  // ==================== GET Operations ====================

  /**
   * Get a company vote proposal name by ID with optional year parameter
   */
  getById(id: string, year?: number): Observable<CompanyVoteProposalNameDto> {
    const params: any = {};
    if (year !== undefined) {
      params.year = year.toString();
    }

    return this.http.get<CompanyVoteProposalNameDto>(`${this.apiUrl}/${id}`, {
      params
    });
  }

  // ==================== POST Operations ====================

  /**
   * Search for company vote proposal names with pagination and filtering
   */
  search(request: CompanyVoteProposalNameSearchFilter): Observable<PaginatedResponse<CompanyVoteProposalNameDto>> {
    return this.http.post<PaginatedResponse<CompanyVoteProposalNameDto>>(`${this.apiUrl}/search`, request);
  }

  /**
   * Export company vote proposal names to CSV based on search criteria
   */
  exportToCsv(request: CompanyVoteProposalNameSearchFilter): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/search/export`, request, {
      responseType: 'blob'
    });
  }

  /**
   * Bulk merge multiple company vote proposal names into one
   */
  bulkMerge(request: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/merge/bulk`, request);
  }

  /**
   * Create a new company vote proposal name
   */
  create(request: CreateCompanyVoteProposalNameRequest): Observable<CompanyVoteProposalNameDto> {
    return this.http.post<CompanyVoteProposalNameDto>(this.apiUrl, request);
  }

  /**
   * Create a new detail for an existing company vote proposal name
   */
  createDetail(id: string, request: CreateCompanyVoteProposalNameRequest): Observable<CompanyVoteProposalNameDto> {
    return this.http.post<CompanyVoteProposalNameDto>(`${this.apiUrl}/${id}`, request);
  }

  // ==================== PUT Operations ====================

  /**
   * Update an existing company vote proposal name (upsert for year)
   */
  update(id: string, request: UpdateCompanyVoteProposalNameRequest): Observable<CompanyVoteProposalNameDto> {
    return this.http.put<CompanyVoteProposalNameDto>(`${this.apiUrl}/${id}`, request);
  }

  // ==================== DELETE Operations ====================

  /**
   * Delete a company vote proposal name and all related data
   */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }
}
